import { Link } from 'react-router-dom';
import Navigation from './components/Navigation';
import ModuleCard from './components/ModuleCard';
import Footer from '../home/components/Footer';
import { useState } from 'react';
import ProjectCases from './components/ProjectCases';

export default function MiddlePage() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');

  const modules = [
    {
      id: 'neural-network',
      icon: 'ri-brain-line',
      title: '神经网络"白盒"实验室',
      description: '3D可视化神经网络,看见AI如何学习',
      color: 'blue',
      features: ['3D可视化', '训练过程演示', '权重变化观察'],
      grade: '9年级',
      level: 'advanced'
    },
    {
      id: 'agent-builder',
      icon: 'ri-robot-2-line',
      title: '智能体搭建工坊',
      description: '低代码创建AI应用,配置人设、知识库、技能',
      color: 'indigo',
      features: ['低代码平台', 'RAG技术', '一键分享'],
      grade: '7-9年级',
      level: 'core'
    },
    {
      id: 'interdisciplinary-project',
      icon: 'ri-team-line',
      title: '跨学科项目空间',
      description: '团队协作,用AI完成创意项目全流程',
      color: 'purple',
      features: ['多模态AI', '团队协作', '项目管理'],
      grade: '7年级',
      level: 'core'
    },
    {
      id: 'iot-simulator',
      icon: 'ri-wifi-line',
      title: '物联网联动模拟器',
      description: '虚拟硬件连线,体验AI+IoT应用',
      color: 'green',
      features: ['可视化编程', '虚拟仿真', 'IoT应用'],
      grade: '7年级',
      level: 'basic'
    },
    {
      id: 'ml-playground',
      icon: 'ri-line-chart-line',
      title: '机器学习游乐场',
      description: '训练简单模型,理解机器学习原理',
      color: 'orange',
      features: ['数据可视化', '模型训练', '效果对比'],
      grade: '8年级',
      level: 'basic'
    },
    {
      id: 'prompt-lab',
      icon: 'ri-chat-3-line',
      title: '提示词工程实验室',
      description: '学习如何与AI高效对话,掌握提示词技巧',
      color: 'cyan',
      features: ['提示词模板', '效果对比', '最佳实践'],
      grade: '7-9年级',
      level: 'basic'
    }
  ];

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!loginForm.username || !loginForm.password) {
      setLoginError('请输入学号和密码');
      return;
    }

    if (loginForm.username.length < 4) {
      setLoginError('学号格式不正确');
      return;
    }

    alert('登录功能正在开发中，请联系学校管理员获取账号信息');
    setShowLoginModal(false);
    setLoginForm({ username: '', password: '' });
  };

  const handleContactSupport = () => {
    setShowLoginModal(false);
    const vapiButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (vapiButton) {
      vapiButton.click();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-6 py-3 rounded-full mb-8">
            <i className="ri-flask-line text-xl"></i>
            <span className="font-semibold">初中版块 · 7-9年级</span>
          </div>
          
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            AI 原理透视与应用
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
            理解机器学习、神经网络原理,使用低代码工具创建AI应用,培养计算思维和创新能力
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-6 w-64">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-eye-line text-3xl text-blue-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">理解</div>
              <div className="text-gray-600">掌握AI原理</div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 w-64">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-tools-line text-3xl text-indigo-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">应用</div>
              <div className="text-gray-600">创建AI工具</div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 w-64">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-group-line text-3xl text-purple-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">协同</div>
              <div className="text-gray-600">团队合作创新</div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">六大核心模块</h2>
            <p className="text-xl text-gray-600">从理论到实践,全面提升AI素养</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {modules.map((module) => (
              <ModuleCard key={module.id} {...module} />
            ))}
          </div>
        </div>
      </section>

      {/* Practice Project Cases */}
      <ProjectCases />

      {/* Learning Objectives */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">学习目标</h2>
            <p className="text-xl text-gray-600">符合浙江省AI教育指导意见要求</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                grade: '7年级',
                icon: 'ri-lightbulb-flash-line',
                title: '初识AI应用',
                objectives: [
                  '了解AI在物联网中的应用',
                  '借助多个AI工具与他人协作开展创意设计',
                  '体验低代码智能体搭建',
                  '理解提示词工程基础'
                ]
              },
              {
                grade: '8年级',
                icon: 'ri-cpu-line',
                title: '深入机器学习',
                objectives: [
                  '理解机器学习的基本概念',
                  '体验数据训练模型的过程',
                  '了解监督学习与无监督学习',
                  '掌握数据可视化方法'
                ]
              },
              {
                grade: '9年级',
                icon: 'ri-brain-line',
                title: '掌握神经网络',
                objectives: [
                  '系统理解神经网络基本原理',
                  '观察神经元激活与权重变化',
                  '能根据实际需求创作低代码智能体',
                  '理解AI伦理与数据安全'
                ]
              },
              {
                grade: '综合能力',
                icon: 'ri-star-line',
                title: '创新与协作',
                objectives: [
                  '培养计算思维和问题解决能力',
                  '提升团队协作与项目管理能力',
                  '掌握跨学科知识整合能力',
                  '养成批判性思维习惯'
                ]
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <div>
                    <div className="text-sm text-blue-600 font-semibold">{item.grade}</div>
                    <div className="text-xl font-bold text-gray-900">{item.title}</div>
                  </div>
                </div>
                <ul className="space-y-3">
                  {item.objectives.map((obj, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                      <span className="text-gray-700">{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-3xl shadow-2xl p-12 text-white">
            <i className="ri-code-box-line text-6xl mb-6"></i>
            <h2 className="text-4xl font-bold mb-6">开启AI深度学习之旅</h2>
            <p className="text-xl mb-8 opacity-90">
              登录后即可访问所有学习模块,从理论到实践全面提升AI素养
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button 
                onClick={() => setShowLoginModal(true)}
                className="px-8 py-4 bg-white text-blue-600 rounded-lg font-bold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                学生登录
              </button>
              <Link to="/" className="px-8 py-4 bg-blue-700 text-white rounded-lg font-bold hover:bg-blue-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer inline-block">
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Login Modal */}
      {showLoginModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowLoginModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">初中生登录</h3>
                    <p className="text-white/80 text-sm">探索AI原理与应用</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowLoginModal(false)}
                  className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-all cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <form onSubmit={handleLogin} className="p-6 space-y-5">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm">
                  <i className="ri-user-line mr-2"></i>学号
                </label>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  placeholder="请输入你的学号"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm">
                  <i className="ri-lock-line mr-2"></i>密码
                </label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="请输入密码"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-all text-sm"
                />
              </div>

              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  {loginError}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">温馨提示：</p>
                    <ul className="space-y-1 text-blue-700">
                      <li>• 学号由学校统一分配</li>
                      <li>• 初始密码为身份证后6位</li>
                      <li>• 如忘记密码，请联系班主任</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-bold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-login-box-line mr-2"></i>
                  立即登录
                </button>
                <button
                  type="button"
                  onClick={handleContactSupport}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-customer-service-2-line mr-2"></i>
                  联系老师
                </button>
              </div>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowLoginModal(false);
                    window.REACT_APP_NAVIGATE('/');
                  }}
                  className="text-sm text-gray-500 hover:text-blue-600 transition-all cursor-pointer"
                >
                  返回首页
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
